import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatMoney, STATUS_META } from "@/lib/format";
import {
  History,
  RotateCcw,
  GitCompare,
  Loader2,
  X,
  ArrowLeft,
  Circle,
  Eye,
} from "lucide-react";

const REASON_LABEL = {
  save: "Save",
  status: "Status change",
  restore: "Restore",
  live: "Current draft",
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

/**
 * Right-hand drawer that shows every saved snapshot of a quote and lets
 * the admin preview / compare against current / restore any past state.
 * Assumes the quote already exists (i.e. not called during first-create).
 */
export default function QuoteVersionsDrawer({
  quoteId,
  open,
  onOpenChange,
  canEdit,
  onRestored,
}) {
  const [versions, setVersions] = useState([]);
  const [viewEvents, setViewEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewV, setPreviewV] = useState(null); // full snapshot dict
  const [comparing, setComparing] = useState(null); // { a, b, diff }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !quoteId) return;
    setPreviewV(null);
    setComparing(null);
    setLoading(true);
    Promise.all([
      api.get(`/quotes/${quoteId}/versions`),
      api.get(`/quotes/${quoteId}`),
    ])
      .then(([v, q]) => {
        setVersions(v.data || []);
        setViewEvents(q.data?.view_events || []);
      })
      .catch((e) =>
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load history"),
      )
      .finally(() => setLoading(false));
  }, [open, quoteId]);

  const openPreview = async (versionNo) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/quotes/${quoteId}/versions/${versionNo}`);
      setPreviewV(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load version");
    } finally {
      setBusy(false);
    }
  };

  const compareAgainstLive = async (versionNo) => {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/quotes/${quoteId}/versions/compare?a=${versionNo}&b=0`,
      );
      setComparing(data);
      setPreviewV(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to compute diff");
    } finally {
      setBusy(false);
    }
  };

  const restore = async (versionNo) => {
    if (!canEdit) return;
    if (!window.confirm(`Restore version V${versionNo}? The current state is saved first.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/versions/${versionNo}/restore`);
      toast.success(`Restored V${versionNo}`);
      onRestored?.(data);
      // Reload list — restore snapshots itself so there are two new entries.
      const { data: fresh } = await api.get(`/quotes/${quoteId}/versions`);
      setVersions(fresh || []);
      setPreviewV(null);
      setComparing(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-white border-l border-[#C4D0DB] text-[#011836] overflow-y-auto p-0"
        data-testid="versions-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-[#DDE5EC] bg-[#F6F7F8]">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-display text-[#011836] text-xl tracking-tight flex items-center gap-2">
              <History className="h-5 w-5 text-[#0088CC]" />
              Version History
            </SheetTitle>
          </div>
          <SheetDescription className="text-[#5B6B7C] text-sm">
            {versions.length} snapshot{versions.length === 1 ? "" : "s"} — auto-saved on every Save
            and status change. Diffs, previews and restore are non-destructive.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="p-8 text-[#5B6B7C] flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
          </div>
        ) : comparing ? (
          <CompareView
            data={comparing}
            onBack={() => setComparing(null)}
          />
        ) : previewV ? (
          <PreviewView
            data={previewV}
            canEdit={canEdit}
            busy={busy}
            onBack={() => setPreviewV(null)}
            onRestore={() => restore(previewV.version_no)}
            onCompare={() => compareAgainstLive(previewV.version_no)}
          />
        ) : (
          <>
            <ProposalOpens events={viewEvents} />
            <VersionsList
              versions={versions}
              canEdit={canEdit}
              busy={busy}
              onPreview={openPreview}
              onCompare={compareAgainstLive}
              onRestore={restore}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function deviceLabel(ua = "") {
  if (/iPhone|iPad/i.test(ua)) return "iPhone/iPad";
  if (/Android/i.test(ua)) return "Android";
  if (/Macintosh/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows";
  return "";
}

function ProposalOpens({ events }) {
  const [expanded, setExpanded] = useState(false);
  const sorted = [...(events || [])].reverse(); // newest first
  const shown = expanded ? sorted : sorted.slice(0, 5);
  return (
    <div className="px-6 pt-5 pb-4 border-b border-[#DDE5EC]" data-testid="proposal-opens-section">
      <div className="flex items-center gap-2 mb-1">
        <Eye className="h-4 w-4 text-[#10B981]" />
        <h3 className="font-display text-sm font-semibold text-[#011836] tracking-wide">
          Proposal link opens
        </h3>
        <span className="text-xs text-[#5B6B7C]" data-testid="proposal-opens-count">
          {sorted.length === 0
            ? "— not opened yet"
            : `${sorted.length} time${sorted.length === 1 ? "" : "s"}`}
        </span>
      </div>
      <p className="text-[11px] text-[#5B6B7C] mb-3">
        Recorded whenever someone opens the emailed / shared proposal link.
        Your own team&apos;s signed-in previews aren&apos;t counted.
      </p>
      {sorted.length > 0 && (
        <div className="space-y-1.5">
          {shown.map((ev, i) => (
            <div
              key={`${ev.at}-${i}`}
              className="flex items-center justify-between text-xs rounded border border-[#DDE5EC] bg-[#F6F7F8] px-3 py-1.5"
              data-testid={`proposal-open-row-${i}`}
            >
              <span className="text-[#011836]">{formatWhen(ev.at)}</span>
              <span className="text-[#5B6B7C]">{deviceLabel(ev.ua)}</span>
            </div>
          ))}
          {sorted.length > 5 && (
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className="text-[11px] text-[#0088CC] hover:underline"
              data-testid="proposal-opens-toggle"
            >
              {expanded ? "Show fewer" : `Show all ${sorted.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function VersionsList({ versions, canEdit, busy, onPreview, onCompare, onRestore }) {
  if (!versions.length) {
    return (
      <div className="p-8 text-[#5B6B7C] text-sm">
        No versions yet — a snapshot is created every time you save or change status.
      </div>
    );
  }
  return (
    <div className="divide-y divide-[#DDE5EC]">
      {versions.map((v, idx) => {
        const isLatest = idx === 0;
        return (
          <div
            key={v.id}
            className="px-6 py-4 hover:bg-[#F1F5F8] transition-colors"
            data-testid={`version-row-${v.version_no}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-base font-semibold text-[#011836]">
                    V{v.version_no}
                  </span>
                  {isLatest && (
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#0088CC]/20 text-[#0088CC] border border-[#0088CC]/30">
                      Latest
                    </span>
                  )}
                  <span
                    className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                      STATUS_META[v.status_at_snapshot]?.className ||
                      "text-[#5B6B7C] border-[#C4D0DB]"
                    }`}
                  >
                    {STATUS_META[v.status_at_snapshot]?.label || v.status_at_snapshot}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-[#7B8494]">
                    {REASON_LABEL[v.reason] || v.reason}
                  </span>
                </div>
                <div className="text-xs text-[#5B6B7C] mt-1">{formatWhen(v.created_at)}</div>
                <div className="text-xs text-[#5B6B7C] mt-0.5">
                  by <span className="text-[#011836]">{v.author_name || "system"}</span>
                </div>
                <div className="text-sm text-[#011836] mt-2 truncate">
                  {v.title || "Untitled"}
                </div>
                <div className="text-xs text-[#5B6B7C] mt-1">
                  {v.line_item_count} line item{v.line_item_count === 1 ? "" : "s"} ·{" "}
                  {formatMoney(v.total || 0, v.currency)}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836] text-xs h-7"
                  onClick={() => onPreview(v.version_no)}
                  disabled={busy}
                  data-testid={`version-preview-btn-${v.version_no}`}
                >
                  Preview
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836] text-xs h-7"
                  onClick={() => onCompare(v.version_no)}
                  disabled={busy}
                  data-testid={`version-compare-btn-${v.version_no}`}
                >
                  <GitCompare className="h-3 w-3 mr-1" /> Compare
                </Button>
                {canEdit && !isLatest && (
                  <Button
                    size="sm"
                    className="bg-[#0088CC] hover:bg-[#006DA6] text-white text-xs h-7"
                    onClick={() => onRestore(v.version_no)}
                    disabled={busy}
                    data-testid={`version-restore-btn-${v.version_no}`}
                  >
                    <RotateCcw className="h-3 w-3 mr-1" /> Restore
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PreviewView({ data, canEdit, busy, onBack, onRestore, onCompare }) {
  const snap = data.snapshot || {};
  const items = snap.line_items || [];
  return (
    <div className="p-6" data-testid="version-preview-view">
      <div className="flex items-center justify-between mb-4">
        <Button
          size="sm"
          variant="ghost"
          className="text-[#5B6B7C] hover:text-[#011836]"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to history
        </Button>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
            onClick={onCompare}
            disabled={busy}
          >
            <GitCompare className="h-4 w-4 mr-1" /> Compare with current
          </Button>
          {canEdit && (
            <Button
              size="sm"
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              onClick={onRestore}
              disabled={busy}
              data-testid="preview-restore-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> Restore this version
            </Button>
          )}
        </div>
      </div>

      <div className="font-display text-xl text-[#011836] font-semibold">V{data.version_no}</div>
      <div className="text-xs text-[#5B6B7C] mt-0.5">
        {formatWhen(data.created_at)} · by {data.author_name || "system"}
      </div>

      <div className="mt-5 space-y-2 text-sm">
        <MetaRow label="Title" value={snap.title || "—"} />
        <MetaRow label="Status" value={STATUS_META[snap.status]?.label || snap.status} />
        <MetaRow label="Currency" value={snap.currency} />
        <MetaRow
          label="GST"
          value={snap.gst_enabled ? `${(snap.gst_rate * 100).toFixed(0)}%` : "Disabled"}
        />
        <MetaRow label="Valid until" value={snap.valid_until || "—"} />
        {snap.notes ? <MetaRow label="Notes" value={snap.notes} /> : null}
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-widest text-[#5B6B7C] mb-2">
          Line items ({items.filter((i) => (i.kind || "item") !== "heading").length})
        </div>
        <div className="rounded border border-[#DDE5EC] divide-y divide-[#DDE5EC]">
          {items.length === 0 && (
            <div className="px-3 py-2 text-sm text-[#5B6B7C]">No line items.</div>
          )}
          {items.map((li, i) => {
            const isHeading = (li.kind || "item") === "heading";
            if (isHeading) {
              return (
                <div
                  key={li.id || i}
                  className="px-3 py-2 bg-[#0088CC]/10 text-[#0088CC] font-display text-sm font-semibold"
                >
                  {li.description || "Section"}
                </div>
              );
            }
            const qty = Number(li.quantity) || 0;
            const price = Number(li.unit_price) || 0;
            return (
              <div key={li.id || i} className="px-3 py-2 text-sm flex justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[#011836] truncate">{li.description || "—"}</div>
                  <div className="text-xs text-[#5B6B7C]">
                    {qty} × {formatMoney(price, snap.currency)}
                  </div>
                </div>
                <div className="text-[#011836] font-medium">
                  {formatMoney(qty * price, snap.currency)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-[#DDE5EC] text-sm space-y-1">
        <div className="flex justify-between text-[#5B6B7C]">
          <span>Subtotal</span>
          <span className="text-[#011836]">{formatMoney(snap.subtotal || 0, snap.currency)}</span>
        </div>
        {snap.gst_enabled && (
          <div className="flex justify-between text-[#5B6B7C]">
            <span>GST</span>
            <span className="text-[#011836]">{formatMoney(snap.gst || 0, snap.currency)}</span>
          </div>
        )}
        <div className="flex justify-between font-display text-base font-semibold pt-1">
          <span>Total</span>
          <span>{formatMoney(snap.total || 0, snap.currency)}</span>
        </div>
      </div>
    </div>
  );
}

function CompareView({ data, onBack }) {
  const { a, b, diff } = data;
  const snapA = a.snapshot || {};
  const snapB = b.snapshot || {};
  const nothingChanged =
    diff.scalar.length === 0 &&
    diff.added.length === 0 &&
    diff.removed.length === 0 &&
    diff.changed.length === 0;

  return (
    <div className="p-6" data-testid="version-compare-view">
      <Button
        size="sm"
        variant="ghost"
        className="text-[#5B6B7C] hover:text-[#011836] mb-4"
        onClick={onBack}
      >
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to history
      </Button>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <SideCard label={`V${a.version_no}`} sub={formatWhen(a.created_at)} snap={snapA} />
        <SideCard
          label={b.version_no === 0 ? "Current" : `V${b.version_no}`}
          sub={b.version_no === 0 ? "Live draft" : formatWhen(b.created_at)}
          snap={snapB}
          accent
        />
      </div>

      {nothingChanged && (
        <div className="text-sm text-[#5B6B7C] px-3 py-2 rounded bg-[#F6F7F8] border border-[#DDE5EC]">
          No differences between these versions.
        </div>
      )}

      {diff.scalar.length > 0 && (
        <DiffSection title="Field changes">
          {diff.scalar.map((s) => (
            <div key={s.field} className="text-sm py-1.5 border-b border-[#DDE5EC] last:border-b-0">
              <div className="text-[10px] uppercase tracking-widest text-[#5B6B7C]">{s.field}</div>
              <div className="grid grid-cols-2 gap-3 mt-0.5">
                <div className="text-red-600/90 line-through decoration-red-500/50 truncate">
                  {String(s.a ?? "—")}
                </div>
                <div className="text-emerald-700 truncate">{String(s.b ?? "—")}</div>
              </div>
            </div>
          ))}
        </DiffSection>
      )}

      {diff.added.length > 0 && (
        <DiffSection title={`Added line items (${diff.added.length})`}>
          {diff.added.map((li, i) => (
            <ItemRow key={i} li={li} tint="added" currency={snapB.currency} />
          ))}
        </DiffSection>
      )}

      {diff.removed.length > 0 && (
        <DiffSection title={`Removed line items (${diff.removed.length})`}>
          {diff.removed.map((li, i) => (
            <ItemRow key={i} li={li} tint="removed" currency={snapA.currency} />
          ))}
        </DiffSection>
      )}

      {diff.changed.length > 0 && (
        <DiffSection title={`Modified line items (${diff.changed.length})`}>
          {diff.changed.map((c, i) => (
            <div key={i} className="py-2 border-b border-[#DDE5EC] last:border-b-0">
              <div className="text-xs text-[#5B6B7C] mb-1">
                {c.before.description || "—"}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded bg-red-500/10 border border-red-500/30 p-2">
                  <ItemLine li={c.before} currency={snapA.currency} />
                </div>
                <div className="rounded bg-emerald-500/10 border border-emerald-500/30 p-2">
                  <ItemLine li={c.after} currency={snapB.currency} />
                </div>
              </div>
            </div>
          ))}
        </DiffSection>
      )}
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[#5B6B7C] text-xs uppercase tracking-widest">{label}</span>
      <span className="text-[#011836] text-sm text-right break-words max-w-[65%]">{value}</span>
    </div>
  );
}

function SideCard({ label, sub, snap, accent }) {
  return (
    <div
      className={`rounded-lg p-3 border ${
        accent ? "border-[#0088CC]/40 bg-[#0088CC]/10" : "border-[#DDE5EC] bg-[#F6F7F8]"
      }`}
    >
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest">
        <Circle
          className={`h-2 w-2 ${accent ? "fill-[#0088CC] text-[#0088CC]" : "fill-[#94A3B8] text-[#5B6B7C]"}`}
        />
        <span className={accent ? "text-[#0088CC]" : "text-[#5B6B7C]"}>{label}</span>
      </div>
      <div className="text-[10px] text-[#5B6B7C] mt-1">{sub}</div>
      <div className="text-sm text-[#011836] mt-1 truncate">{snap.title || "—"}</div>
      <div className="text-xs text-[#5B6B7C]">
        {STATUS_META[snap.status]?.label || snap.status} ·{" "}
        {formatMoney(snap.total || 0, snap.currency)}
      </div>
    </div>
  );
}

function DiffSection({ title, children }) {
  return (
    <div className="mb-5">
      <div className="text-xs uppercase tracking-widest text-[#5B6B7C] mb-2">{title}</div>
      <div className="rounded border border-[#DDE5EC] px-3 bg-[#F6F7F8]">{children}</div>
    </div>
  );
}

function ItemRow({ li, tint, currency }) {
  const qty = Number(li.quantity) || 0;
  const price = Number(li.unit_price) || 0;
  const cls =
    tint === "added"
      ? "text-emerald-700"
      : tint === "removed"
      ? "text-red-600 line-through decoration-red-500/40"
      : "text-[#011836]";
  return (
    <div className="text-sm py-1.5 border-b border-[#DDE5EC] last:border-b-0 flex justify-between gap-2">
      <div className={`min-w-0 truncate ${cls}`}>{li.description || "—"}</div>
      <div className={`shrink-0 ${cls}`}>
        {qty} × {formatMoney(price, currency)}
      </div>
    </div>
  );
}

function ItemLine({ li, currency }) {
  const qty = Number(li.quantity) || 0;
  const price = Number(li.unit_price) || 0;
  const cost = Number(li.cost_price) || 0;
  const margin = Number(li.margin_pct) || 0;
  return (
    <div className="space-y-0.5 text-[#011836]">
      <div>Qty: {qty}</div>
      <div>Cost: {formatMoney(cost, currency)}</div>
      <div>Margin: {margin}%</div>
      <div>Unit price: {formatMoney(price, currency)}</div>
      <div className="font-medium">= {formatMoney(qty * price, currency)}</div>
    </div>
  );
}
